import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { NoShowShift } from '../api/types';
import { formatMarkedAt, formatShiftDateTime } from '../util/dateFormat';

import Avatar from './Avatar';

// One no-show card = a shift header (Client · Date & time · Skill · Location)
// with an expand affordance, and the no-show worker rows beneath it (a shift
// can carry multiple no-show workers). Defaults to expanded, matching the
// Figma; the chevron collapses/expands the worker rows.

type Props = { shift: NoShowShift };

const NoShowShiftCard = ({ shift }: Props) => {
  const [open, setOpen] = useState(true);
  const tz = shift.timezone;

  const col = (label: string, value: string, modifier: string) => (
    <div className={`dashv2-nscol dashv2-nscol--${modifier}`}>
      <p className="dashv2-nslabel">{label}</p>
      <p className="dashv2-nsvalue" title={value}>
        {value || '—'}
      </p>
    </div>
  );

  return (
    <div className="dashv2-nscard">
      <div className="dashv2-nshead">
        {col('Client', shift.client_name || '', 'client')}
        {col(
          'Date & time',
          formatShiftDateTime(shift.start_at, shift.end_at, tz),
          'date',
        )}
        {col('Skill', shift.skill_name || '', 'skill')}
        {col('Location', shift.location_code || '', 'loc')}
        <button
          type="button"
          className={`dashv2-nsexpand${open ? ' is-open' : ''}`}
          aria-label={open ? 'Collapse workers' : 'Expand workers'}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronRightIcon />
        </button>
      </div>

      {open && (
        <div className="dashv2-nsworkers">
          {shift.workers.map((w) => (
            <div className="dashv2-nsrow" key={w.id}>
              <div className="dashv2-nsuser">
                <Avatar name={w.name} src={w.avatar_url} />
                <span className="dashv2-nsname">
                  {w.name || 'Unknown worker'}
                </span>
              </div>
              <div className="dashv2-nsfield dashv2-nsfield--status">
                <p className="dashv2-nslabel">Status</p>
                <span className="dashv2-nsbadge">No show</span>
              </div>
              <div className="dashv2-nsfield dashv2-nsfield--grow">
                <p className="dashv2-nslabel">Marked by</p>
                <p className="dashv2-nsfield-val">{w.marked_by || '—'}</p>
              </div>
              <div className="dashv2-nsfield dashv2-nsfield--grow">
                <p className="dashv2-nslabel">Date &amp; time marked</p>
                <p className="dashv2-nsfield-val">
                  {formatMarkedAt(w.marked_at, tz)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoShowShiftCard;
